<script lang="ts">
  import { computePosition, flip, shift, offset } from '@floating-ui/dom';
  import type { Task } from '../lib/types';
  import {
    copyTasksWithToast,
    describeTaskCopyFields,
    TASK_COPY_FORMAT_LABELS,
    type TaskCopyFormat,
  } from '../lib/taskClipboard';
  import { taskCopyPreferences } from '../lib/stores/taskCopyPreferences';
  import TaskCopyFieldsPicker from './TaskCopyFieldsPicker.svelte';
  import Icon from './Icon.svelte';

  interface Props {
    /** Tasks to copy, in display order (callers pass incomplete tasks only). */
    tasks: Task[];
    /** Markdown `#` heading for the copied tasks. */
    heading?: string;
    /** Accessible label / tooltip for the trigger. */
    label?: string;
  }

  let { tasks, heading, label = 'Copy tasks' }: Props = $props();

  let isOpen = $state(false);
  // 'formats' — copy actions; 'include' — drilled into the field checkboxes
  let view = $state<'formats' | 'include'>('formats');
  let triggerEl: HTMLButtonElement | undefined = $state();
  let menuEl: HTMLDivElement | undefined = $state();

  // Anchor the fixed-position menu to the trigger. Writes node styles directly
  // (no reactive state) so positioning can't feed back into rendering.
  // Re-runs when the view changes since the menu's height changes with it.
  function anchorToTrigger(node: HTMLDivElement, _view: typeof view) {
    function position() {
      if (!triggerEl) return;
      computePosition(triggerEl, node, {
        strategy: 'fixed',
        placement: 'bottom-end',
        middleware: [offset(4), flip(), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        node.style.left = `${x}px`;
        node.style.top = `${y}px`;
      });
    }
    position();
    return { update: position };
  }

  // Let the click bubble so other open menus/popovers see it as an outside
  // click and close; handleWindowClick ignores clicks on this trigger.
  function toggle() {
    isOpen = !isOpen;
    view = 'formats';
    returnedFromInclude = false;
  }

  // Keep keyboard focus in the menu when a view switch removes the focused button.
  let returnedFromInclude = $state(false);
  function focusIf(shouldFocus: boolean) {
    return (node: HTMLButtonElement) => {
      if (shouldFocus) node.focus();
    };
  }

  function handleCopy(format: TaskCopyFormat) {
    isOpen = false;
    void copyTasksWithToast(tasks, format, heading);
  }

  // composedPath() rather than contains(): switching views removes the clicked
  // button from the DOM before this window listener runs.
  function handleWindowClick(e: MouseEvent) {
    if (!isOpen) return;
    const path = e.composedPath();
    if ((menuEl && path.includes(menuEl)) || (triggerEl && path.includes(triggerEl))) return;
    isOpen = false;
  }

  // Like the Move-to-project drill-in, Escape first steps back out of Include….
  function handleKeydown(e: KeyboardEvent) {
    if (!isOpen || e.key !== 'Escape') return;
    if (view === 'include') {
      view = 'formats';
      returnedFromInclude = true;
      return;
    }
    isOpen = false;
    triggerEl?.focus();
  }
</script>

<svelte:window onclick={handleWindowClick} onkeydown={handleKeydown} />

<button
  type="button"
  class="copy-trigger"
  class:active={isOpen}
  onclick={toggle}
  title={label}
  aria-label={label}
  aria-haspopup="menu"
  aria-expanded={isOpen}
  bind:this={triggerEl}
>
  <Icon name="clipboard" size={12} />
</button>

{#if isOpen}
  <div
    class="copy-menu"
    role={view === 'formats' ? 'menu' : 'group'}
    aria-label={view === 'formats' ? undefined : 'Copy settings'}
    bind:this={menuEl}
    use:anchorToTrigger={view}
  >
    {#if view === 'formats'}
      <span class="copy-menu-title">Copy {tasks.length} as</span>
      {#each Object.entries(TASK_COPY_FORMAT_LABELS) as [format, formatLabel]}
        <button
          type="button"
          role="menuitem"
          class="copy-menu-item"
          onclick={() => handleCopy(format as TaskCopyFormat)}
        >
          {formatLabel}
        </button>
      {/each}
      <div class="copy-menu-divider" role="separator"></div>
      <button
        type="button"
        role="menuitem"
        class="copy-menu-item include-item"
        aria-haspopup="true"
        onclick={() => (view = 'include')}
        {@attach focusIf(returnedFromInclude)}
      >
        <span class="include-label">Include… <span aria-hidden="true">›</span></span>
        <span class="include-summary">{describeTaskCopyFields($taskCopyPreferences)}</span>
      </button>
    {:else}
      <button
        type="button"
        class="copy-menu-back"
        aria-label="Back to copy formats"
        onclick={() => {
          view = 'formats';
          returnedFromInclude = true;
        }}
        {@attach focusIf(true)}
      >
        <span aria-hidden="true">‹</span> Include
      </button>
      <TaskCopyFieldsPicker />
    {/if}
  </div>
{/if}

<style>
  .copy-trigger {
    width: 1.125rem;
    height: 1.125rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .copy-trigger:hover,
  .copy-trigger.active {
    color: #4b5563;
    background: rgba(0, 0, 0, 0.06);
  }

  .copy-menu {
    position: fixed;
    top: 0;
    left: 0;
    min-width: 8.5rem;
    padding: 0.25rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    z-index: 80;
  }

  .copy-menu-title {
    display: block;
    padding: 0.1875rem 0.375rem;
    font-size: 0.5625rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .copy-menu-item {
    display: block;
    width: 100%;
    padding: 0.3125rem 0.375rem;
    font-size: 0.6875rem;
    font-weight: 500;
    text-align: left;
    color: #374151;
    background: none;
    border: none;
    border-radius: 0.3125rem;
    cursor: pointer;
  }

  .copy-menu-item:hover {
    background: #f3f4f6;
  }

  .copy-menu-divider {
    height: 1px;
    margin: 0.1875rem 0.25rem;
    background: #f3f4f6;
  }

  .include-label {
    display: flex;
    justify-content: space-between;
    color: #4b5563;
  }

  .include-summary {
    display: block;
    max-width: 9rem;
    margin-top: 0.0625rem;
    overflow: hidden;
    font-size: 0.5625rem;
    font-weight: 400;
    color: #9ca3af;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .copy-menu-back {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    width: 100%;
    padding: 0.1875rem 0.375rem;
    font-size: 0.5625rem;
    font-weight: 600;
    color: #6b7280;
    text-align: left;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: none;
    border: none;
    border-radius: 0.3125rem;
    cursor: pointer;
  }

  .copy-menu-back span {
    font-size: 0.75rem;
    line-height: 1;
  }

  .copy-menu-back:hover {
    color: #374151;
    background: #f3f4f6;
  }
</style>
